// @flow

// This component was copied from https://github.com/mpowaga/react-slider to 'modernize' and fix some issues, mostly related to perf
// Also, some redundant functionality was removed
import React, {Component} from 'react';
import classNames from 'classnames';

import type {Element} from 'react';

type Position = [number, number];
export type Bounds = Array<number>;
export type Props = {
    /**
     * The minimum value of the slider.
     */
    min: number,
    /**
     * The maximum value of the slider.
     */
    max: number,
    /**
     * Value to be added or subtracted on each step the slider makes.
     * Must be greater than zero.
     * `max - min` should be evenly divisible by the step value.
     */
    step: number,
    /**
     * The minimal distance between any pair of handles.
     * Must be positive, but zero means they can sit on top of each other.
     */
    minDistance: number,
    /**
     * Determines the initial positions of the handles and the number of handles.
     *
     * If a number is passed a slider with one handle will be rendered.
     * If an array is passed each value will determine the position of one handle.
     * The values in the array must be sorted.
     */
    defaultValue: Bounds,
    /**
     * Like `defaultValue` but for [controlled components](http://facebook.github.io/react/docs/forms.html#controlled-components).
     */
    value?: Bounds,
    ariaLabel?: string | Array<string>,
    ariaValuetext?: string,
    /**
     * The css class set on the slider node.
     */

    className: string,
    /**
     * The css class set on each handle node.
     *
     * In addition each handle will receive a numbered css class of the form `${handleClassName}-${i}`,
     * e.g. `handle-0`, `handle-1`, ...
     */
    handleClassName: string,
    /**
     * The css class set on the handle that is currently being moved.
     */
    handleActiveClassName: string,
    /**
     * If `true` bars between the handles will be rendered.
     */
    withBars: boolean,
    /**
     * The css class set on the bars between the handles.
     * In addition bar fragment will receive a numbered css class of the form `${barClassName}-${i}`,
     * e.g. `bar-0`, `bar-1`, ...
     */
    barClassName: string,
    /**
     * If `true` the handles can't be moved.
     */
    disabled: boolean,
    /**
     * Disables handle move when clicking the slider bar
     */
    snapDragDisabled: boolean,
    /**
     * Callback called on every value change.
     */
    onChange?: (bounds: Bounds) => void,
    /**
     * Callback called before starting to move a handle.
     */
    onBeforeChange?: (bounds: Bounds) => void,
    /**
     * Callback called only after moving a handle has ended.
     */
    onAfterChange?: (bounds: Bounds) => void,
    /**
     * Handle render function
     */
    renderHandle?: (index: number) => Element<*>
};

export type State = {
    index: number,
    upperBound: number,
    sliderLength: number,
    value: Bounds,
    zIndices: Array<number>,
    sliderStart: number,
    handleSize: number,
    startPosition: number,
    startValue: number
};

/**
 * To prevent text selection while dragging.
 * http://stackoverflow.com/questions/5429827/how-can-i-prevent-text-element-selection-with-cursor-drag
 */
function pauseEvent(e: SyntheticEvent<*>) {
    if (e.stopPropagation) e.stopPropagation();
    if (e.preventDefault) e.preventDefault();
    return false;
}

function stopPropagation(e: SyntheticEvent<*>) {
    if (e.stopPropagation) e.stopPropagation();
}

function isEqualValues(a: ?Array<number>, b: ?Array<number>): boolean {
    if (a && b) {
        return (
            a === b ||
            // $FlowFixMe
            (a.length === b.length && !a.some((value, i) => value !== b[i]))
        );
    }

    return false;
}

export default class ReactSlider extends Component<Props, State> {
    static defaultProps = {
        min: 0,
        max: 100,
        step: 1,
        minDistance: 0,
        defaultValue: [0],
        className: 'slider',
        handleClassName: 'handle',
        handleActiveClassName: 'active',
        barClassName: 'bar',
        withBars: false,
        disabled: false,
        snapDragDisabled: false
    };

    constructor(props: Props) {
        super(props);
        let value = this._or(this.props.value, this.props.defaultValue);
        // reused throughout the component to store results of iterations over `value`
        this.tempArray = value.slice();
        this.state = this.getInitialState(value);
    }

    getInitialState = (value: Array<number>) => {
        let zIndices = [];
        for (let i = 0; i < value.length; i++) {
            value[i] = this._trimAlignValue(value[i], this.props);
            zIndices.push(i);
        }

        return {
            index: -1,
            upperBound: 0,
            sliderLength: 0,
            sliderStart: 0,
            handleSize: 0,
            startPosition: 0,
            startValue: 0,
            value,
            zIndices
        };
    };

    componentDidMount() {
        window.addEventListener('resize', this._handleResize);
        this._resize();
    }

    // Keep the internal `value` consistent with an outside `value` if present.
    // This basically allows the slider to be a controlled component.
    componentWillReceiveProps(newProps: Props) {
        let value = this._or(newProps.value, this.state.value);
        const stateValue = this.state.value.slice();
        // ensure the array keeps the same size as `value`
        this.tempArray = value.slice();

        for (let i = 0; i < value.length; i++) {
            stateValue[i] = this._trimAlignValue(value[i], newProps);
        }

        if (stateValue.length > value.length) {
            stateValue.length = value.length;
        }

        this.setState({
            value: stateValue
        });

        // If an upperBound has not yet been determined (due to the component being hidden
        // during the mount event, or during the last resize), then calculate it now
        if (this.state.upperBound === 0) {
            this._resize();
        }
    }

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        return (
            nextState.index !== this.state.index ||
            nextState.sliderLength !== this.state.sliderLength ||
            nextState.upperBound !== this.state.upperBound ||
            !isEqualValues(nextState.value, this.state.value) ||
            !isEqualValues(nextState.zIndices, this.state.zIndices) ||
            nextProps.min !== this.props.min ||
            nextProps.max !== this.props.max ||
            nextProps.step !== this.props.step ||
            nextProps.minDistance !== this.props.minDistance ||
            !isEqualValues(nextProps.value, this.props.value)
        );
    }

    componentWillUnmount() {
        this._clearPendingResizeTimeouts();
        window.removeEventListener('resize', this._handleResize);
    }

    cache: {[string]: Function} = {};
    tempArray: any[] = [];
    pendingResizeTimeouts: any[] = [];
    hasMoved: boolean = false;
    handleRefs: Array<?HTMLDivElement> = [];
    sliderRef: {current: null | HTMLDivElement} = React.createRef();

    // just returns `value` if present and `defaultValue` otherwise
    // TODO: better name? better solution?
    _or = (value?: Bounds, defaultValue: Bounds): Bounds => {
        return value && value.length > 0 ? value : defaultValue;
    };

    _resize = () => {
        let slider = (this.sliderRef && this.sliderRef.current) || {};
        let handle = this.handleRefs[0];
        if (slider && handle) {
            let rect = slider.getBoundingClientRect();
            let sliderMax = rect.right;
            let sliderMin = rect.left;

            this.setState({
                upperBound: slider.clientWidth,
                sliderLength: Math.abs(sliderMax - sliderMin),
                handleSize: handle.clientWidth,
                sliderStart: sliderMin
            });
        }
    };

    _handleResize = () => {
        let resizeTimeout = requestAnimationFrame(() => {
            // drop this request from pendingResizeTimeouts to reduce memory usage
            this.pendingResizeTimeouts.shift();
            this._resize();
        });

        this.pendingResizeTimeouts.push(resizeTimeout);
    };

    // clear all pending timeouts to avoid error messages after unmounting
    _clearPendingResizeTimeouts = () => {
        do {
            let nextFrameRequest = this.pendingResizeTimeouts.shift();

            cancelAnimationFrame(nextFrameRequest);
        } while (this.pendingResizeTimeouts.length);
    };

    // calculates the offset of a handle in pixels based on its value.
    _calcOffset = (value: number): number => {
        let range = this.props.max - this.props.min;
        if (range === 0) {
            return 0;
        }
        let ratio = (value - this.props.min) / range;
        return ratio * this.state.upperBound;
    };

    // calculates the value corresponding to a given pixel offset, i.e. the inverse of `_calcOffset`.
    _calcValue = (offset: number): number =>
        (offset / this.state.upperBound) * (this.props.max - this.props.min) +
        this.props.min;

    _buildHandleStyle = (offset: number, i: number): {} => ({
        position: 'absolute',
        willChange: this.state.index >= 0 ? 'left' : '',
        zIndex: this.state.zIndices.indexOf(i) + 1,
        left: `${offset}px`
    });

    _buildBarStyle = (min: number, max: number): {} => ({
        position: 'absolute',
        willChange: this.state.index >= 0 ? 'left,right' : '',
        left: min,
        right: max
    });

    _getClosestIndex = (pixelOffset: number): number => {
        let minDist = Number.MAX_VALUE;
        let closestIndex = -1;

        let value = this.state.value;
        let l = value.length;

        for (let i = 0; i < l; i++) {
            let offset = this._calcOffset(value[i]);
            let dist = Math.abs(pixelOffset - offset);
            if (dist < minDist) {
                minDist = dist;
                closestIndex = i;
            }
        }

        return closestIndex;
    };

    _calcOffsetFromPosition = (position: number): number => {
        let pixelOffset = position - this.state.sliderStart;

        pixelOffset -= this.state.handleSize / 2;
        return pixelOffset;
    };

    // Snaps the nearest handle to the value corresponding to `position` and calls `callback` with that handle's index.
    _forceValueFromPosition = (position: number, callback: Function) => {
        let pixelOffset = this._calcOffsetFromPosition(position);
        let closestIndex = this._getClosestIndex(pixelOffset);
        let nextValue = this._trimAlignValue(this._calcValue(pixelOffset));

        let value = this.state.value.slice(); // Clone this.state.value since we'll modify it temporarily
        value[closestIndex] = nextValue;

        // Prevents the slider from shrinking below `props.minDistance`
        for (let i = 0; i < value.length - 1; i += 1) {
            if (value[i + 1] - value[i] < this.props.minDistance) return;
        }

        this.setState({value}, callback.bind(this, closestIndex));
    };

    _getMousePosition = (e: SyntheticMouseEvent<*>): Position => [
        e.pageX,
        e.pageY
    ];

    _getTouchPosition = (e: SyntheticTouchEvent<*>): Position => {
        let touch = e.touches[0];
        return this._getMousePosition(touch);
    };

    _getKeyDownEventMap = () => {
        return {
            keydown: this._onKeyDown,
            focusout: this._onBlur
        };
    };

    _getMouseEventMap = () => {
        return {
            mousemove: this._onMouseMove,
            mouseup: this._onMouseUp
        };
    };

    _getTouchEventMap = () => {
        return {
            touchmove: this._onTouchMove,
            touchend: this._onTouchEnd
        };
    };

    _memoize = (key: string, fn: Function, ...args: any[]): Function => {
        let cb = this.cache[key];

        if (cb) {
            return cb;
        }

        cb = fn.bind(this, ...args);
        this.cache[key] = cb;
        return cb;
    };

    // create the `keydown` handler for the i-th handle
    _createOnKeyDown = (i: number): Function => {
        return this._memoize(`onKeyDown-${i}`, this._onKeyDownHandler, i);
    };

    _onKeyDownHandler = (index: number, e: SyntheticKeyboardEvent<*>) => {
        if (this.props.disabled) return;
        this._start(index);
        this._addHandlers(this._getKeyDownEventMap());
        pauseEvent(e);
    };

    // create the `mousedown` handler for the i-th handle
    _createOnMouseDown = (i: number): Function => {
        return this._memoize(`onMouseDown-${i}`, this._onMouseDownHandler, i);
    };

    _onMouseDownHandler = (index: number, e: SyntheticMouseEvent<*>) => {
        if (this.props.disabled) return;
        let position = this._getMousePosition(e);
        this._start(index, position[0]);
        this._addHandlers(this._getMouseEventMap());
        pauseEvent(e);
    };

    // create the `touchstart` handler for the i-th handle
    _createOnTouchStart = (i: number): Function => {
        return this._memoize(`onTouchStart-${i}`, this._onTouchStartHandler, i);
    };

    _onTouchStartHandler = (index: number, e: SyntheticTouchEvent<*>) => {
        if (this.props.disabled || e.touches.length > 1) return;
        let position = this._getTouchPosition(e);
        this._start(index, position[0]);
        this._addHandlers(this._getTouchEventMap());
        stopPropagation(e);
    };

    _addHandlers = (eventMap: Object) => {
        Object.keys(eventMap).forEach((key) => {
            window.addEventListener(key, eventMap[key], false);
        });
    };

    _removeHandlers = (eventMap: Object) => {
        Object.keys(eventMap).forEach((key) => {
            window.removeEventListener(key, eventMap[key], false);
        });
    };

    _start = (i: number, position?: number) => {
        let activeEl = document.activeElement;
        let handleRef = this.handleRefs[i];
        // if activeElement is body window will lost focus in IE9
        /* istanbul ignore next */
        if (
            activeEl &&
            handleRef &&
            activeEl !== document.body &&
            activeEl !== handleRef
        ) {
            handleRef.focus();
        }

        this.hasMoved = false;

        this._fireChangeEvent('onBeforeChange');

        let zIndices = this.state.zIndices.slice();
        zIndices.splice(zIndices.indexOf(i), 1); // remove wherever the element is
        zIndices.push(i); // add to end

        this.setState(function(prevState) {
            return {
                startValue: this.state.value[i],
                startPosition: Number.isInteger(position)
                    ? position
                    : prevState.startPosition,
                index: i,
                zIndices
            };
        });
    };

    _onMouseUp = () => {
        this._onEnd(this._getMouseEventMap());
    };

    _onTouchEnd = () => {
        this._onEnd(this._getTouchEventMap());
    };

    _onBlur = () => {
        this._onEnd(this._getKeyDownEventMap());
    };

    _onEnd = (eventMap: Object) => {
        this._removeHandlers(eventMap);
        this.setState(
            {index: -1},
            this._fireChangeEvent.bind(this, 'onAfterChange')
        );
    };

    _onMouseMove = (e: SyntheticMouseEvent<*>) => {
        let position = this._getMousePosition(e);
        let diffPosition = this._getDiffPosition(position[0]);
        let newValue = this._getValueFromPosition(diffPosition);
        this._move(newValue);
    };

    _onTouchMove = (e: SyntheticTouchEvent<*>) => {
        if (e.touches.length > 1) return;

        let position = this._getTouchPosition(e);

        pauseEvent(e);

        let diffPosition = this._getDiffPosition(position[0]);
        let newValue = this._getValueFromPosition(diffPosition);

        this._move(newValue);
    };

    _onKeyDown = (e: SyntheticKeyboardEvent<*>) => {
        if (e.ctrlKey || e.shiftKey || e.altKey) return;
        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
                pauseEvent(e);
                this._moveDownOneStep();
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                pauseEvent(e);
                this._moveUpOneStep();
                break;
            case 'Home':
                this._move(this.props.min);
                break;
            case 'End':
                this._move(this.props.max);
                break;
            default:
                return;
        }
    };

    _moveUpOneStep = () => {
        let oldValue = this.state.value[this.state.index];
        let newValue = oldValue + this.props.step;
        this._move(Math.min(newValue, this.props.max));
    };

    _moveDownOneStep = () => {
        let oldValue = this.state.value[this.state.index];
        let newValue = oldValue - this.props.step;
        this._move(Math.max(newValue, this.props.min));
    };

    _getValueFromPosition = (position: number): number => {
        let diffValue =
            (position / (this.state.sliderLength - this.state.handleSize)) *
            (this.props.max - this.props.min);
        return this._trimAlignValue(this.state.startValue + diffValue);
    };

    _getDiffPosition = (position: number): number =>
        position - this.state.startPosition;

    _move = (newValue: number) => {
        this.hasMoved = true;

        let props = this.props;
        let state = this.state;
        let index = state.index;

        let value = state.value;
        let length = value.length;
        let oldValue = value[index];

        let minDistance = props.minDistance;

        if (index > 0) {
            let valueBefore = value[index - 1];
            if (newValue < valueBefore + minDistance) {
                newValue = valueBefore + minDistance;
            }
        }

        if (index < length - 1) {
            let valueAfter = value[index + 1];
            if (newValue > valueAfter - minDistance) {
                newValue = valueAfter - minDistance;
            }
        }

        value[index] = newValue;

        // Normally you would use `shouldComponentUpdate`, but since the slider is a low-level component,
        // the extra complexity might be worth the extra performance.
        if (newValue !== oldValue) {
            this.setState(
                {value},
                this._fireChangeEvent.bind(this, 'onChange')
            );
        }
    };

    _trimAlignValue = (val: number, props?: Props): number => {
        return this._alignValue(this._trimValue(val, props), props);
    };

    _trimValue = (val: number, props?: Props): number => {
        props = props || this.props;

        if (val <= props.min) val = props.min;
        if (val >= props.max) val = props.max;

        return val;
    };

    _alignValue = (val: number, props?: Props): number => {
        props = props || this.props;

        let valModStep = (val - props.min) % props.step;
        let alignValue = val - valModStep;

        if (Math.abs(valModStep) * 2 >= props.step) {
            alignValue += valModStep > 0 ? props.step : -props.step;
        }

        return parseFloat(alignValue.toFixed(5));
    };

    _createHandleRef = (i: number): Function => {
        return this._memoize(`handleRef-${i}`, this._setHandleRef, i);
    };

    _setHandleRef = (index: number, component: HTMLDivElement) => {
        this.handleRefs[index] = component;
    };

    _renderHandle = (style: {}, child: ?Element<*>, i: number) => {
        let className = classNames(
            this.props.handleClassName,
            `${this.props.handleClassName}-${i}`,
            {
                [this.props.handleActiveClassName]: this.state.index === i
            }
        );

        return (
            <div
                ref={this._createHandleRef(i)}
                key={`handle${i}`}
                className={className}
                style={style}
                onMouseDown={this._createOnMouseDown(i)}
                onTouchStart={this._createOnTouchStart(i)}
                onFocus={this._createOnKeyDown(i)}
                tabIndex={0}
                role={'slider'}
                aria-valuenow={this.state.value[i]}
                aria-valuemin={this.props.min}
                aria-valuemax={this.props.max}
                aria-disabled={this.props.disabled}
                aria-label={
                    Array.isArray(this.props.ariaLabel)
                        ? this.props.ariaLabel[i]
                        : this.props.ariaLabel
                }
                aria-valuetext={this.props.ariaValuetext}
            >
                {child}
            </div>
        );
    };

    _renderHandles = (offset: Array<number>) => {
        let length = offset.length;
        let i;
        let styles = this.tempArray.slice();
        for (i = 0; i < length; i++) {
            styles[i] = this._buildHandleStyle(offset[i], i);
        }

        let res = [];
        for (i = 0; i < length; i++) {
            let handle = null;

            if (this.props.renderHandle) {
                handle = this.props.renderHandle(i);
            }
            res[i] = this._renderHandle(styles[i], handle, i);
        }
        return res;
    };

    _renderBar = (
        i: number,
        offsetFrom: number,
        offsetTo: number
    ): Element<*> => {
        const className = classNames(
            this.props.barClassName,
            `${this.props.barClassName}-${i}`
        );

        return (
            <div
                key={`bar${i}`}
                className={className}
                style={this._buildBarStyle(
                    offsetFrom,
                    this.state.upperBound - offsetTo
                )}
            />
        );
    };

    _renderBars = (offset: Array<number>) => {
        let bars = [];
        let lastIndex = offset.length - 1;

        bars.push(this._renderBar(0, 0, offset[0]));

        for (let i = 0; i < lastIndex; i++) {
            bars.push(this._renderBar(i + 1, offset[i], offset[i + 1]));
        }

        bars.push(
            this._renderBar(
                lastIndex + 1,
                offset[lastIndex],
                this.state.upperBound
            )
        );

        return bars;
    };

    _onSliderMouseDown = (e: SyntheticMouseEvent<*>) => {
        if (this.props.disabled) return;
        this.hasMoved = false;
        if (!this.props.snapDragDisabled) {
            let position = this._getMousePosition(e);
            this._forceValueFromPosition(position[0], (i) => {
                this._start(i, position[0]);
                this._fireChangeEvent('onChange');
                this._addHandlers(this._getMouseEventMap());
            });
        }

        pauseEvent(e);
    };

    _fireChangeEvent = (event: string) => {
        if (this.props[event]) {
            this.props[event](this.state.value);
        }
    };

    render() {
        const state = this.state;
        const props = this.props;
        const className = classNames(props.className, {
            disabled: props.disabled
        });

        let offset = this.tempArray;
        let value = state.value;
        let l = value.length;
        for (let i = 0; i < l; i++) {
            offset[i] = this._calcOffset(value[i]);
        }

        let bars = props.withBars ? this._renderBars(offset) : null;
        let handles = this._renderHandles(offset);

        return (
            <div
                ref={this.sliderRef}
                style={{position: 'relative'}}
                className={className}
                onMouseDown={this._onSliderMouseDown}
            >
                {bars}
                {handles}
            </div>
        );
    }
}
